/**
 * Reed.ai Design System — Alert
 *
 * Variants: success | error | warning
 * Sizes:    desktop (40px) | mobile (32px)
 *
 * Source: Figma "Reed.ai - Global components" — Alerts page
 * Nodes:  953:5162 (success), 968:2094 (error)
 *
 * Design details:
 *  Container — pill-shaped: rounded-[20px], px-[24px] py-[8px], inline-flex centered row
 *  Success   — CircleCheck icon (#22C55E green stroke), piccadilly-blue text #081351
 *  Error     — AlertTriangle icon (#EF4444 red), piccadilly-blue text #081351
 *  Background — transparent / surface (no solid fill)
 *  Label     — Red Hat Text Medium 14px (mobile) / 16px (desktop)
 */

import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AlertVariant = 'success' | 'error' | 'warning';
export type AlertSize    = 'desktop' | 'mobile';

export interface AlertProps {
  /** Visual style of the alert */
  variant: AlertVariant;
  /** Message to display */
  message: string;
  /** Desktop = 16px text, Mobile = 14px text */
  size?: AlertSize;
  className?: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function SuccessIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <circle cx="10" cy="10" r="9" stroke="#22C55E" strokeWidth="1.5" />
      <path
        d="M6 10.5L8.5 13L14 7.5"
        stroke="#22C55E"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M8.574 2.993a1.618 1.618 0 0 1 2.852 0l6.52 11.602C18.655 15.62 17.74 17 16.52 17H3.48C2.26 17 1.345 15.62 2.054 14.595L8.574 2.993Z"
        stroke="#EF4444"
        strokeWidth="1.5"
      />
      <path
        d="M10 8v3.5"
        stroke="#EF4444"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
      <circle cx="10" cy="14" fill="#EF4444" r="0.75" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M8.574 2.993a1.618 1.618 0 0 1 2.852 0l6.52 11.602C18.655 15.62 17.74 17 16.52 17H3.48C2.26 17 1.345 15.62 2.054 14.595L8.574 2.993Z"
        stroke="#F59E0B"
        strokeWidth="1.5"
      />
      <path
        d="M10 8v3.5"
        stroke="#F59E0B"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
      <circle cx="10" cy="14" fill="#F59E0B" r="0.75" />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reed.ai Alert
 *
 * @example
 * // Success alert
 * <Alert variant="success" message="Your profile has been updated." />
 *
 * // Error alert (mobile size)
 * <Alert variant="error" message="Something went wrong." size="mobile" />
 */
export function Alert({
  variant,
  message,
  size = 'desktop',
  className,
}: AlertProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-[20px] px-6 py-2',
        'border',
        variant === 'success'
          ? 'border-[#22C55E]/30 bg-[#F0FDF4]'
          : variant === 'error'
            ? 'border-[#EF4444]/30 bg-[#FEF2F2]'
            : 'border-[#F59E0B]/30 bg-[#FFFBEB]',
        className,
      )}
      role="alert"
    >
      {variant === 'success' ? (
        <SuccessIcon />
      ) : variant === 'error' ? (
        <ErrorIcon />
      ) : (
        <WarningIcon />
      )}
      {/* Keep !text-text-primary: tailwind-merge must not let conflicting text utilities override Alert copy. */}
      <span
        className={cn(
          'font-body font-medium !text-text-primary leading-[1.5]',
          size === 'desktop' ? 'text-body-md' : 'text-body-md-m',
        )}
      >
        {message}
      </span>
    </div>
  );
}

export default Alert;
